import React, { useState } from 'react';
import { Box, Button, Collapse, Link, Typography } from '@mui/material';
import MapOutlinedIcon from '@mui/icons-material/MapOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import {
  mapUnavailableBackdropSx,
  mapUnavailableCardSx,
  mapUnavailableHintSx,
  mapUnavailableIconBoxSx,
  mapUnavailableIconSx,
  mapUnavailableInlineSx,
  mapUnavailableLinkSx,
  mapUnavailableListSx,
  mapUnavailableStageSx,
  mapUnavailableTextSx,
  mapUnavailableTitleSx,
  mapUnavailableToggleSx,
} from './MapUnavailable.styles';

export const MAP_UNAVAILABLE_TITLE = 'El mapa no se puede mostrar en este navegador';
export const OPEN_IN_TERRIO_LABEL = 'Abrir en Terrio';
export const MAP_CRASH_TITLE = 'No pudimos mostrar el mapa';

const HARDWARE_STEPS = [
  'Chrome o Edge: Configuración → Sistema → activá «Usar aceleración de gráficos cuando esté disponible» y reiniciá el navegador.',
  'Firefox: Ajustes → General → Rendimiento → desmarcá «Usar la configuración recomendada» y activá «Usar aceleración por hardware».',
  'Safari: actualizá macOS / Safari; la aceleración viene activada.',
  'Si sigue igual, probá con otro navegador actualizado o desde el celular.',
];

const POLICY_TEXT = 'Parece una computadora administrada por tu empresa: la aceleración gráfica (WebGL) está bloqueada por una política. No lo podés cambiar desde el navegador; pedile a sistemas que habilite WebGL, o abrí Terrio desde otro equipo o el celular.';
const POLICY_HINT = '¿Es una computadora del trabajo? Puede estar bloqueado por una política de la empresa: consultá con sistemas.';

function HowToFix({ reason }) {
  if (reason === 'enterprise_policy') {
    return <Typography sx={mapUnavailableHintSx}>{POLICY_TEXT}</Typography>;
  }
  return (
    <>
      <Box component="ul" sx={mapUnavailableListSx}>
        {HARDWARE_STEPS.map((step) => <li key={step}>{step}</li>)}
      </Box>
      <Typography sx={mapUnavailableHintSx}>{POLICY_HINT}</Typography>
    </>
  );
}

function OpenInTerrioLink({ href }) {
  if (!href) return null;
  return (
    <Link
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      underline="hover"
      sx={mapUnavailableLinkSx}
    >
      {OPEN_IN_TERRIO_LABEL}
      <OpenInNewIcon sx={{ fontSize: 14 }} aria-hidden />
    </Link>
  );
}

/**
 * Aviso cuando el navegador no puede crear WebGL (MapLibre no arranca).
 *
 * - `page`: fondo de `/app` (queda debajo del header y los paneles).
 * - `stage`: ocupa el lugar del mapa / plano (Showroom).
 * - `inline`: aviso chico arriba de la lista o la ficha en los share.
 *
 * `compact` lo achica para el iframe de la landing; `openInTerrioHref` suma
 * «Abrir en Terrio» en pestaña nueva (solo se pasa cuando está embebido).
 *
 * @param {{
 *   variant?: 'page'|'stage'|'inline',
 *   reason?: string|null,
 *   compact?: boolean,
 *   openInTerrioHref?: string|null,
 *   sx?: object,
 * }} props
 */
export default function MapUnavailable({
  variant = 'page',
  reason = null,
  compact = false,
  openInTerrioHref = null,
  sx = null,
}) {
  const [showHelp, setShowHelp] = useState(false);
  const helpToggle = (
    <Button
      size="small"
      variant="text"
      onClick={() => setShowHelp((open) => !open)}
      aria-expanded={showHelp}
      sx={mapUnavailableToggleSx}
    >
      {showHelp ? 'Ocultar' : 'Cómo activarlo'}
    </Button>
  );

  if (variant === 'inline') {
    return (
      <Box role="status" data-testid="map-unavailable" sx={sx ? { ...mapUnavailableInlineSx, ...sx } : mapUnavailableInlineSx}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <MapOutlinedIcon sx={{ ...mapUnavailableIconSx, fontSize: 18 }} aria-hidden />
          <Typography sx={{ ...mapUnavailableTextSx, flex: '1 1 160px' }}>
            El mapa no está disponible en este navegador.
          </Typography>
          {!compact ? helpToggle : null}
          <OpenInTerrioLink href={openInTerrioHref} />
        </Box>
        {!compact ? (
          <Collapse in={showHelp} unmountOnExit>
            <HowToFix reason={reason} />
          </Collapse>
        ) : null}
      </Box>
    );
  }

  const crashed = reason === 'map_crash';
  const card = (
    <Box role="status" data-testid="map-unavailable" sx={mapUnavailableCardSx(compact)}>
      <Box sx={mapUnavailableIconBoxSx}>
        <MapOutlinedIcon sx={mapUnavailableIconSx} aria-hidden />
      </Box>
      <Typography component="h2" sx={mapUnavailableTitleSx(compact)}>
        {crashed ? MAP_CRASH_TITLE : MAP_UNAVAILABLE_TITLE}
      </Typography>
      <Typography sx={mapUnavailableTextSx}>
        {crashed && 'Recargá la página para volver a intentarlo.'}
        {!crashed && (reason === 'enterprise_policy'
          ? 'Tu navegador tiene bloqueada la aceleración gráfica.'
          : 'Tu navegador no tiene activada la aceleración gráfica (WebGL), que el mapa necesita.')}
        {variant === 'page' ? ' El resto de Terrio funciona igual.' : ''}
      </Typography>
      {compact || crashed ? null : helpToggle}
      {compact || crashed ? null : (
        <Collapse in={showHelp} unmountOnExit sx={{ width: '100%' }}>
          <HowToFix reason={reason} />
        </Collapse>
      )}
      <OpenInTerrioLink href={openInTerrioHref} />
    </Box>
  );

  return (
    <Box sx={sx ? { ...(variant === 'stage' ? mapUnavailableStageSx : mapUnavailableBackdropSx), ...sx } : (variant === 'stage' ? mapUnavailableStageSx : mapUnavailableBackdropSx)}>
      {card}
    </Box>
  );
}
